// 他参加者の「操作中（未確定）」のゴースト／プレビューを描画するレイヤ。
// - object.transient.transform: ドラッグ移動 / リサイズ / 回転中の枠を破線ゴーストで表示
// - pen.stroke.append: 描画中ペンストロークを折れ線で表示
//
// transient store（useCanvasTransientStore）を購読し、リアルタイムに追従する。
// 自分自身の操作分は描画しない（自分の画面では実 DOM が既に動いているため）。

import { useEffect, type CSSProperties } from "react";
import type { CanvasObject } from "@refinement-canvas/domain";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";
import {
  useCanvasTransientStore,
  type RemotePenPreview,
  type RemoteTransformPreview,
} from "./store/canvas-transient-store";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "../../lib/canvas-coordinates";
import {
  REMOTE_TRANSIENT_PRUNE_INTERVAL_MS,
  REMOTE_TRANSIENT_STALE_MS,
} from "./remote-transient-timing";
import {
  buildStrokeCenterlinePath,
  needsDottedStrokeStartFallback,
  strokeDashArray,
} from "../../lib/stroke-pattern";

interface Props {
  readonly currentUserId: string;
}

// 描画中ストロークのゴーストの太さ（ステージ座標 px、確定時の正確な太さは未受信のため概算）。
const PEN_PREVIEW_THICKNESS_PX = 4;
const PREVIEW_LAYER_Z_INDEX = Number.MAX_SAFE_INTEGER - 2;
// 名前ラベルの上端を、ゴースト（回転前の外接矩形）の上辺からどれだけ上へ離すか（画面 px）。
// 逆スケールのラッパの中で使うため、拡大率に依らず画面上この距離になる（拡大率 100% ではステージ px の top: -22px と同じ位置）。
const GHOST_LABEL_OFFSET_PX = 22;

export function CanvasRemotePreviewsLayer({ currentUserId }: Props): JSX.Element {
  const transforms = useCanvasTransientStore((s) => s.transforms);
  const penStrokes = useCanvasTransientStore((s) => s.penStrokes);
  const prune = useCanvasTransientStore((s) => s.prune);
  const objects = useCanvasDocStore((s) => s.objects);
  const viewers = useCanvasViewersStore((s) => s.viewers);
  const now = Date.now();

  // 操作終了後に残るゴーストを定期的に掃除する（確定 op での破棄を取りこぼした場合の保険）。
  useEffect(() => {
    const id = window.setInterval(
      () => prune(REMOTE_TRANSIENT_STALE_MS),
      REMOTE_TRANSIENT_PRUNE_INTERVAL_MS
    );
    return () => window.clearInterval(id);
  }, [prune]);

  return (
    <div
      data-testid="canvas-remote-previews-layer"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: PREVIEW_LAYER_Z_INDEX,
      }}
    >
      <svg
        width={CANVAS_WIDTH_PX}
        height={CANVAS_HEIGHT_PX}
        style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible" }}
      >
        {Object.entries(penStrokes).map(([strokeId, stroke]) => {
          if (stroke.userId === currentUserId) return null;
          if (now - stroke.updatedAt > REMOTE_TRANSIENT_STALE_MS) return null;
          const color = normalizeColor(viewers[stroke.userId]?.color);
          return <RemotePenStroke key={strokeId} color={color} stroke={stroke} />;
        })}
      </svg>

      {Object.entries(transforms).map(([objectId, preview]) => {
        if (preview.userId === currentUserId) return null;
        if (now - preview.updatedAt > REMOTE_TRANSIENT_STALE_MS) return null;
        const base = objects[objectId];
        if (!base) return null;
        const viewer = viewers[preview.userId];
        return (
          <RemoteTransformGhost
            key={objectId}
            base={base}
            color={normalizeColor(viewer?.color)}
            displayName={viewer?.displayName}
            preview={preview}
          />
        );
      })}
    </div>
  );
}

function RemotePenStroke({
  color,
  stroke,
}: {
  readonly color: string;
  readonly stroke: RemotePenPreview;
}): JSX.Element | null {
  if (stroke.points.length === 0) return null;
  const path = buildStrokeCenterlinePath(stroke.points);
  const dotted = stroke.strokePattern === "dotted";
  const fallback = needsDottedStrokeStartFallback(
    stroke.points,
    PEN_PREVIEW_THICKNESS_PX,
    stroke.strokePattern
  );
  const first = stroke.points[0];
  return (
    <g data-testid={`remote-pen-${stroke.strokeId}`} opacity={0.85}>
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={PEN_PREVIEW_THICKNESS_PX}
        strokeDasharray={
          dotted ? strokeDashArray(PEN_PREVIEW_THICKNESS_PX, stroke.strokePattern).join(" ") : undefined
        }
        // ペンのプレビューは実線も従来どおり丸端を維持し、点線も各点を丸く表示する。
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {fallback && first ? (
        <circle
          cx={first.x}
          cy={first.y}
          r={PEN_PREVIEW_THICKNESS_PX / 2}
          fill={color}
        />
      ) : null}
    </g>
  );
}

function RemoteTransformGhost({
  base,
  color,
  displayName,
  preview,
}: {
  readonly base: CanvasObject;
  readonly color: string;
  readonly displayName: string | undefined;
  readonly preview: RemoteTransformPreview;
}): JSX.Element {
  // プレビューに含まれない辺は確定済みオブジェクトの値で補う。
  const x = preview.x ?? base.x;
  const y = preview.y ?? base.y;
  const w = preview.w ?? base.w;
  const h = preview.h ?? base.h;
  const rotation = preview.rotation ?? base.rotation ?? 0;

  const containerStyle: CSSProperties = {
    position: "absolute",
    left: `${x}px`,
    top: `${y}px`,
    width: `${w}px`,
    height: `${h}px`,
    pointerEvents: "none",
  };
  const frameStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    // 破線枠の線幅は拡大率に依らず、他参加者の選択枠（canvas-remote-selections-layer）と同じ補正で見せる。
    // 枠は図形の外接矩形そのもので scale のラッパへ包めないため、線幅だけを逆補正する。
    // 角丸は選択枠と同じくステージ px のまま残す（背景の塗りは外接矩形を塗るだけで、補正の対象ではない）。
    border: `calc(2px * var(--canvas-inv-zoom, 1)) dashed ${color}`,
    borderRadius: "8px",
    boxSizing: "border-box",
    background: withAlpha(color, 0.08),
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    transformOrigin: "center",
  };

  return (
    <div
      data-testid={`remote-transform-ghost-${preview.userId}-${base.id}`}
      style={containerStyle}
    >
      <div
        aria-hidden="true"
        data-testid={`remote-transform-ghost-frame-${preview.userId}-${base.id}`}
        style={frameStyle}
      />
      {displayName ? (
        // 名前ラベルの大きさだけをステージの scale(zoom) から切り離し、拡大率に依らず 100% 相当の
        // 画面サイズで表示する（他参加者のカーソル・選択バッジと同じ逆スケール）。ゴースト上辺からの
        // 距離は scale の右側の translateY で表す（右側の関数は点へ先に適用されてから 1/zoom されるため、
        // 書いた px がそのまま画面上で効く）。上端を基準にするため、ラベルの高さ（文字サイズ・行高）に依らず、
        // ゴースト上辺からラベル上端までが画面上 GHOST_LABEL_OFFSET_PX に保たれる。
        <div
          data-testid={`remote-transform-ghost-label-scale-${preview.userId}-${base.id}`}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            transform: `scale(var(--canvas-inv-zoom, 1)) translateY(${-GHOST_LABEL_OFFSET_PX}px)`,
            transformOrigin: "0 0",
          }}
        >
          <div
            data-testid={`remote-transform-ghost-label-${preview.userId}-${base.id}`}
            style={{
              padding: "1px 6px",
              background: color,
              color: "#ffffff",
              fontSize: "11px",
              fontWeight: 700,
              borderRadius: "4px",
              whiteSpace: "nowrap",
              boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            {displayName}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function normalizeColor(color: string | undefined): string {
  return color && color.trim().length > 0 ? color : "#1e63d4";
}

function withAlpha(color: string, alpha: number): string {
  if (!/^#[0-9a-f]{6}$/iu.test(color)) return `rgba(30, 99, 212, ${alpha})`;
  const red = Number.parseInt(color.slice(1, 3), 16);
  const green = Number.parseInt(color.slice(3, 5), 16);
  const blue = Number.parseInt(color.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
